import { getParentOfType, Instance, SnapshotIn, types } from "mobx-state-tree";

import { ReactNode } from "react";
import { Schema, ValidationError } from "yup";
import { withSetPropAction } from "../../../../mobx-stores/_extension/with-set-props";

/**
 * Enumeration of supported input types for dynamic form controls
 * Each type corresponds to a specific UI component and behavior
 */
export enum InputType {
  none = "none", // Hidden/no render control
  text = "text", // Standard text input
  datePicker = "datePicker", // Date picker component
  password = "password", // Password input with masking
  number = "number", // Numeric input with formatting
  select = "select", // Dropdown selection
  checkbox = "checkbox", // Boolean checkbox
  array = "array", // Dynamic array of nested controls
  autoCompleteMultiple = "autoCompleteMultiple", // Multi-select autocomplete
  autoComplete = "autoComplete", // Single-select autocomplete
  textArea = "textArea", // Multi-line text input
  label = "label", // Read-only display label
  radioGroup = "radioGroup", // Radio button group
}

export type AutoCompleteMode = "legacy" | "auto" | "paged";

export type AutoCompleteDomain =
  | "customer"
  | "order"
  | "workOrder"
  | "product"
  | "material"
  | "paper";

export type AutoCompleteSearchRequest = {
  pageIndex: number;
  pageSize: number;
};

export type AutoCompleteSearchResult = {
  items?: OptionItem[];
  totalCount?: number;
};

export type AutoCompleteSelectedValue = string | number;

/**
 * Metadata interface for additional control configuration
 * Contains type-specific properties for enhanced functionality
 */
export interface MetaData {
  /** Number of rows for textarea controls */
  rows?: number;
  /** Async function to fetch autocomplete options based on search keyword */
  searchOptionItemsAsync?: (keyword: string) => Promise<OptionItem[]>;
  /** Optional paged search function for autocomplete */
  searchOptionItemsPagedAsync?: (
    keyword: string,
    request: AutoCompleteSearchRequest,
  ) => Promise<AutoCompleteSearchResult>;
  /** Optional selected value resolver for hydrating label when value is missing in current options */
  resolveSelectedOptionAsync?: (
    value: AutoCompleteSelectedValue,
  ) => Promise<OptionItem | null>;
  /** Optional mode hint for autocomplete renderer selection */
  autoCompleteMode?: AutoCompleteMode;
  /** Optional domain hint for provider based autocomplete */
  autoCompleteDomain?: AutoCompleteDomain;
  /** Optional page size override when paged mode is enabled */
  autoCompletePageSize?: number;
  /** Fixed decimal places for numeric inputs */
  fixDecimalScale?: number;

  disabledSearchOnFocus?: boolean; // Whether to trigger search when autocomplete gains focus
  enableSearchWhenRender?: boolean; // Whether to trigger search immediately when autocomplete is rendered
}

/**
 * Interface for option items used in select, autocomplete, and radio controls
 */
export interface OptionItem {
  value: any; // The actual value stored
  label: string; // Display text for the option
  renderOption?: ReactNode; // Custom render component for complex options
}

// Late binding for circular dependency resolution in MobX-State-Tree
export const ControlLater = types.late((): any => Control);

// Type definition for nested children controls (2D array structure)
export const ChildrenType = types.array(types.array(ControlLater));
export interface IChildrenType extends Instance<typeof ChildrenType> {}

/**
 * Main Control model using MobX-State-Tree
 * Represents a single form control with validation, state management, and nested children support
 */
export const Control = types
  .model({
    /** Unique identifier and form field name */
    name: "",
    /** Display label for the control */
    label: "",
    /** Placeholder text for input controls */
    placeholder: "",
    /** Optional small help text shown via an info tooltip next to the field */
    helpText: types.maybe(types.string),
    /** Initial/default value of the control */
    defaultValue: types.frozen<any>(),
    /** Current value of the control */
    value: types.frozen<any>(),
    /** Type of input control to render */
    type: types.maybeNull(
      types.enumeration<InputType>("type", Object.values(InputType)),
    ),
    /** Whether the control is disabled/read-only */
    disabled: false,
    /** Grid size for responsive layout (1-12) */
    gridSize: 12,
    /** Yup validation schema for the control */
    validattionYupSchema: types.maybeNull(types.frozen<Schema>()),
    /** Current validation error if any */
    validationError: types.maybeNull(types.frozen<ValidationError>()),
    /** Array of selectable options for select/autocomplete controls */
    optionItems: types.optional(types.frozen<OptionItem[]>(), []),
    /** Additional metadata for type-specific configuration */
    metaData: types.maybeNull(types.frozen<MetaData>()),
    /** Schema definition for child controls (used in array type) */
    childSchema: types.maybeNull(types.frozen<any>()),
    /** Nested child controls arranged in rows */
    children: types.optional(ChildrenType, []),
  })
  .views((self) => ({
    /**
     * Computed view to check if all nested children are valid
     * @returns true if all child controls pass validation
     */
    get isChildrenValid() {
      let isValid = true;
      if (self.children && self.children.length > 0) {
        self.children.forEach((row) => {
          if (!isValid) {
            return;
          }
          row.forEach((control) => {
            const ctr = control as IControl;
            if (ctr.validationError != null) {
              isValid = false;
              return;
            }
          });
        });
      }
      return isValid;
    },
    /**
     * Computed view to check if any nested children have been modified
     * @returns true if any child control value differs from its default
     */
    get isChildrenDirty() {
      let isDirty = false;
      if (self.children && self.children.length > 0) {
        self.children.forEach((child) => {
          if (isDirty) {
            return;
          }
          child.forEach((control) => {
            const ctr = control as IControl;
            if (ctr.defaultValue !== ctr.value) {
              isDirty = true;
              return;
            }
          });
        });
      }
      return isDirty;
    },
  }))
  .views((self) => ({
    /**
     * Computed view for overall validation status
     * @returns true if both this control and all children are valid
     */
    get isValid() {
      return self.validationError == null && self.isChildrenValid;
    },
    /**
     * Computed view for overall dirty status
     * @returns true if this control or any children have been modified
     */
    get isDirty() {
      if (self.children && self.children.length) {
        return self.value !== self.defaultValue || self.isChildrenDirty;
      } else {
        return self.value !== self.defaultValue;
      }
    },
  }))
  .actions(withSetPropAction)
  .actions((self) => ({
    /**
     * Sets both default and current value simultaneously
     * Used during form initialization
     * @param val - The value to set as both default and current
     */
    setDefaultValue: (val: any) => {
      self.defaultValue = val;
      self.value = val;
    },
  }))
  .actions((self) => {
    /**
     * Validates a single control using its Yup schema
     * @param control - The control to validate
     */
    const validateControl = async (control: IControl) => {
      try {
        if (!control.validattionYupSchema) return true;
        await control.validattionYupSchema.validate(control.value);
        control.setProp("validationError", null);
      } catch (err) {
        const validationError = err as ValidationError;
        control.setProp("validationError", validationError);
      }
    };

    /**
     * Validates all nested child controls recursively
     * @param control - The parent control containing children
     */
    const validateChildren = async (control: IControl) => {
      if (control.children && control.children.length > 0) {
        const propmises: Promise<any>[] = [];
        control.children.forEach((child) => {
          return child.forEach((control) => {
            propmises.push(validateControl(control as IControl));
          });
        });

        await Promise.all(propmises);
      }
    };

    /**
     * Validates this control and all its children
     * @returns Promise that resolves when validation is complete
     */
    const validate = async () => {
      await Promise.all([
        validateControl(self as IControl),
        validateChildren(self as IControl),
      ]);
    };
    return { validate };
  })
  .actions((self) => ({
    /**
     * Sets the schema definition for child controls
     * Used for array-type controls to define the structure of each array item
     * @param schema - Schema object defining child control properties
     */
    setChildSchema: (schema: any) => {
      self.childSchema = schema;
    },

    /**
     * Adds multiple child rows based on provided data
     * Creates controls for each data item using the child schema
     * @param dataSet - Array of data objects to create child controls from
     */
    addChildren: <T>(dataSet: T[]) => {
      if (self.type !== InputType.array) {
        console.error(`The control name ${self.name} is not array type`);
        return;
      }

      dataSet.forEach((data: any) => {
        const chidren: IControlProperties[] = [];
        Object.keys(self.childSchema).forEach((key) => {
          const schema = self.childSchema[key];
          const control: IControlProperties = {
            ...schema,
            value: data[key] || schema.defaultValue,
            name: key,
          };
          chidren.push(control);
        });

        self.children.push(chidren);
      });
    },

    /**
     * Adds a new empty child row with default values
     * Used when user clicks "Add" button in array controls
     */
    addNewChild: () => {
      const chidren: IControlProperties[] = [];
      Object.keys(self.childSchema).forEach((key) => {
        const schema = self.childSchema[key];
        const control: IControlProperties = {
          ...schema,
          value: schema.defaultValue,
          name: key,
        };
        chidren.push(control);
      });

      self.children.push(chidren);
    },

    /**
     * Removes a child row by its index
     * @param idx - Index of the row to remove
     */
    removeChildByIndex: (idx: number) => {
      self.children.splice(idx, 1);
    },

    /**
     * Sets the control value and triggers validation
     * This is the primary method for updating control values from UI
     * @param value - New value to set
     */
    setValueAndValidate: async (value: any) => {
      self.value = value;
      await self.validate();
    },

    /**
     * Gets the parent control in the tree hierarchy
     * @returns Parent control instance or undefined
     */
    getParent: () => {
      return getParentOfType(self, Control);
    },

    /**
     * Sets the entire children array
     * @param children - New children array to set
     */
    setChildren: (children: IChildrenType) => {
      self.children = children;
    },
  }));

// Type definitions for external usage
export interface IControl extends Instance<typeof Control> {}
export interface IControlProperties extends SnapshotIn<typeof Control> {}
